import { Bold, Portal, Title, useOnOutsideClick } from '@panwds/react-ui';
import { twMerge } from '@panwds/tailwindcss';
import { animated, useSpring } from '@react-spring/web';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigationContextV3 } from '../NavigationContextV3/NavigationContextV3.js';
import { getFlyoutListClasses, getFlyoutRootClasses, getFlyoutTitleClasses } from './flyout.logic.js';
const VIEWPORT_MARGIN = 2; // Margin from viewport edges in px
const TITLE_HEIGHT = 40; // Space occupied by title in px
const DOUBLE_VIEWPORT_MARGIN = VIEWPORT_MARGIN * 2;
const calculateAvailableSpace = (menuRects, bottomMargin, topMargin) => ({
    below: window.innerHeight - menuRects.top - bottomMargin - VIEWPORT_MARGIN,
    above: menuRects?.bottom - topMargin - VIEWPORT_MARGIN
});
const calculateExtraScrollHeight = (flyoutScrollHeight, availableSpace) => flyoutScrollHeight -
    availableSpace -
    (flyoutScrollHeight > window.innerHeight ? flyoutScrollHeight - window.innerHeight + DOUBLE_VIEWPORT_MARGIN : 0);
export function FlyOutMenuV3({ title, menuRef, children, topMargin = 0, bottomMargin = 0, emptyState, isOpen = false, navRef, className }) {
    const menuRects = menuRef?.current?.getBoundingClientRect();
    const flyoutRef = useRef(null);
    const { leftNavCollapsed, subNavCollapsed, setState, flyoutId, openSearchFlyout } = useNavigationContextV3();
    const [flyoutInfo, setFlyoutInfo] = useState({
        isPositionedBottom: false,
        extraScrollHeight: 0,
        adjustedMaxHeight: 0
    });
    useOnOutsideClick(navRef || [], (event) => {
        if (!leftNavCollapsed)
            return;
        if (!subNavCollapsed)
            return;
        if (navRef &&
            !navRef.current?.contains(event.target) &&
            (flyoutId || openSearchFlyout) &&
            !flyoutRef?.current?.contains(event.target)) {
            setState((prev) => ({
                ...prev,
                visitedNav: undefined,
                flyoutId: undefined,
                flyOutRef: undefined,
                openSearchFlyout: false
            }));
        }
    });
    useLayoutEffect(() => {
        if (isOpen && flyoutRef.current) {
            flyoutRef.current.style.display = 'block';
        }
    }, [isOpen]);
    useLayoutEffect(() => {
        const ulElement = flyoutRef.current?.querySelector('ul');
        if (ulElement && menuRects) {
            const flyoutScrollHeight = ulElement.scrollHeight + TITLE_HEIGHT;
            const availableSpace = calculateAvailableSpace(menuRects, bottomMargin, topMargin);
            const isPositionedBottom = availableSpace.below > availableSpace.above;
            const extraScrollHeight = calculateExtraScrollHeight(flyoutScrollHeight, isPositionedBottom ? availableSpace.below : availableSpace.above);
            const maxAvailableHeight = isPositionedBottom ? availableSpace.below : availableSpace.above;
            const adjustedMaxHeight = Math.max(maxAvailableHeight, maxAvailableHeight + extraScrollHeight);
            setFlyoutInfo((prev) => ({
                ...prev,
                isPositionedBottom,
                extraScrollHeight,
                adjustedMaxHeight
            }));
        }
    }, [children]);
    const { isPositionedBottom, extraScrollHeight, adjustedMaxHeight } = flyoutInfo;
    const fadeAnimation = useSpring({
        opacity: isOpen ? 1 : 0,
        config: { duration: 180 },
        onRest: () => {
            if (!isOpen && flyoutRef.current) {
                flyoutRef.current.style.display = 'none';
            }
        }
    });
    if (!menuRects)
        return null;
    return (React.createElement(Portal, null,
        React.createElement(animated.div, { ref: flyoutRef, "data-testid": "flyout", style: {
                ...fadeAnimation,
                [isPositionedBottom ? 'top' : 'bottom']: isPositionedBottom
                    ? (menuRects?.top ?? 0) - topMargin - (extraScrollHeight > 0 ? extraScrollHeight : 0)
                    : window.innerHeight -
                        (menuRects?.bottom ?? 0) +
                        bottomMargin -
                        (extraScrollHeight > 0 ? extraScrollHeight : 0),
                maxHeight: `${adjustedMaxHeight}px`,
                boxShadow: '0px 2px 12px 0px rgba(20, 26, 31, 0.88)',
                pointerEvents: isOpen ? 'auto' : 'none'
            }, className: twMerge(`${getFlyoutRootClasses()} twds-transition-[top,bottom] twds-duration-180 twds-ease-in-out`, className?.root) }, children && (React.createElement(React.Fragment, null,
            title && (React.createElement(Title, { level: 3, size: "xxs", addClassName: getFlyoutTitleClasses(), truncate: true },
                React.createElement(Bold, null, title))),
            React.Children.count(children) === 0 && emptyState ? emptyState : null,
            React.createElement("ul", { className: getFlyoutListClasses(title), style: {
                    maxHeight: `${adjustedMaxHeight - TITLE_HEIGHT - 8}px`
                } }, children))))));
}
//# sourceMappingURL=FlyOutMenuV3.js.map